import React from 'react';
import { Calendar, Droplet as DropletHalf, Wrench, Droplets, Building } from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import CallToAction from '../components/CallToAction';

const ServicePage: React.FC = () => {
  const services = [
    {
      id: 'weekly',
      title: 'Weekly Cleaning',
      description: 'Our comprehensive weekly pool cleaning service includes water testing and chemical balancing, skimming of debris from the surface, emptying skimmer and pump baskets, brushing walls and steps, vacuuming the pool floor, checking and backwashing the filter as needed, and inspecting all equipment for proper operation.',
      icon: <Calendar className="h-6 w-6" />,
      link: '/quote?service=weekly',
      details: [
        'Water testing & chemical balancing',
        'Skimming surface debris',
        'Emptying skimmer & pump baskets',
        'Brushing walls & steps',
        'Vacuuming pool floor',
        'Filter inspection & maintenance',
        'Equipment checks',
        'Detailed service reports'
      ]
    },
    {
      id: 'biweekly',
      title: 'Bi-Weekly Cleaning',
      description: 'Perfect for pools with lower usage or those with automatic pool cleaners. Our bi-weekly service includes all the same thorough cleaning and maintenance as our weekly service, but performed every other week to accommodate your specific needs.',
      icon: <DropletHalf className="h-6 w-6" />,
      link: '/quote?service=biweekly',
      details: [
        'Same comprehensive service as weekly',
        'Performed every other week',
        'Ideal for pools with less frequent use',
        'Perfect for homes with automatic cleaners',
        'Budget-friendly option',
        'Customized chemical treatments',
        'Seasonal adjustments',
        'Equipment inspections'
      ],
      image: 'https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      id: 'repairs',
      title: 'Pool Repairs',
      description: 'Our experienced technicians can diagnose and repair all types of pool equipment issues. From pump and motor replacements to heater repairs and filter system troubleshooting, we provide fast and reliable service to keep your pool running smoothly.',
      icon: <Wrench className="h-6 w-6" />,
      link: '/quote?service=repairs',
      details: [
        'Pump & motor repairs/replacement',
        'Filter system troubleshooting',
        'Heater diagnosis & repair',
        'Salt system maintenance',
        'Plumbing leak detection & repair',
        'Lighting repairs & upgrades',
        'Automation system servicing',
        'Energy efficiency consultation'
      ]
    },
    {
      id: 'green',
      title: 'Green to Clean',
      description: 'Has your pool turned green or become neglected? Our specialized service will restore your pool to crystal clear condition. We use advanced techniques and treatments to address algae, balance chemicals, clean surfaces, and restore your pool water to pristine condition.',
      icon: <Droplets className="h-6 w-6" />,
      link: '/quote?service=green',
      details: [
        'Specialized algae treatments',
        'Advanced shock treatments',
        'Physical debris removal',
        'Thorough brushing of all surfaces',
        'Multiple filter cleanings',
        'Water rebalancing',
        'Follow-up maintenance recommendations',
        'Preventative advice'
      ]
    },
    {
      id: 'rental',
      title: 'Short Term Rental Care',
      description: 'Keep your vacation rental pool in perfect condition for your guests. Our service includes flexible scheduling to work around guest stays, expedited response for urgent issues, detailed inspections before guest arrivals, and emergency services when needed.',
      icon: <Building className="h-6 w-6" />,
      link: '/quote?service=rental',
      details: [
        'Flexible scheduling around bookings',
        'Pre-arrival inspections & cleaning',
        'Post-guest servicing',
        'On-call service for guest issues',
        'Detailed logs for property managers',
        'Chemical storage & management',
        'Equipment monitoring',
        'Seasonal service adjustments'
      ],
      image: 'https://images.pexels.com/photos/261327/pexels-photo-261327.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative bg-purple-900 py-20">
        <div 
          className="absolute inset-0 z-0 opacity-30" 
          style={{
            backgroundImage: 'url("https://images.pexels.com/photos/261318/pexels-photo-261318.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Comprehensive pool care solutions tailored to your needs. From routine maintenance to specialized treatments, we keep your pool sparkling clean.
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.map((service) => (
              <ServiceCard 
                key={service.id}
                id={service.id}
                title={service.title}
                description={service.description}
                icon={service.icon}
                link={service.link}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Sections */}
      {services.map((service) => (
        <section key={service.id} id={service.id} className="py-16 bg-gray-50 scroll-mt-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className={`${service.id % 2 === 0 ? 'order-2' : 'order-1'}`}>
                <div className="bg-purple-100 p-3 inline-block rounded-full text-purple-700 mb-4">
                  {service.icon}
                </div>
                <h2 className="text-3xl font-bold text-purple-900 mb-4">{service.title}</h2>
                <p className="text-gray-700 mb-6">{service.description}</p>
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-xl font-semibold text-purple-800 mb-4">What's Included:</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {service.details.map((detail, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <span className="h-2 w-2 bg-orange-500 rounded-full mr-2"></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={service.link}
                  className="inline-block px-6 py-3 bg-orange-500 text-white font-bold rounded-md hover:bg-orange-600 transition-colors duration-200"
                >
                  Get a Quote
                </a>
              </div>
              {service.id === 'biweekly' && service.image && (
                <div className={`${service.id % 2 === 0 ? 'order-1' : 'order-2'}`}>
                  <div className="rounded-lg overflow-hidden shadow-xl">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-auto transform hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Customized Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-purple-900 mb-4">Custom Service Plans</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Don't see exactly what you need? We offer customized service plans tailored to your specific pool requirements.
          </p>
          <a
            href="/quote"
            className="inline-block px-6 py-3 bg-orange-500 text-white font-bold rounded-md hover:bg-orange-600 transition-colors duration-200"
          >
            Contact Us for Custom Options
          </a>
        </div>
      </section>

      <CallToAction />
    </div>
  );
};

export default ServicePage;